import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";

// Copy supplied verbatim by the owner (2026-09-28). Preserve FD2G-FAC-R01,
// the 12-month term, the 45–60 minute renewal description, the 80% passing
// score, the 60/30/7-day + expiration reminder schedule (NO automation built),
// the same certification number on renewal, and the three reinstatement
// pathways. No CCCHAPS, no pricing, CE credits, accreditation, testimonials,
// statistics. No renewal enrollment, payment, records, or verification tools.

const ctaCls =
  "inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 whitespace-nowrap";
const ctaGoldCls =
  "inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover transition-colors duration-200 whitespace-nowrap";

// 2026-10-20: Active Facilitation Requirement, Documentation and Verification,
// Renewal and Continued Competency, and Exceptions and Extensions added
// verbatim from the owner. Additive only — FD2G-FAC-R01, the four renewal
// parts, the 80% assessment, the 12-month cycle, and all reinstatement
// pathways remain unchanged.

function ActiveFacilitation() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24" data-testid="renewal-active-facilitation">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          The Active Facilitation Requirement
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            The From Diagnosis to Goodbye&trade; Certified Facilitator
            credential represents more than completion of a training course.
            It reflects an ongoing commitment to compassionate communication,
            ethical facilitation, and meaningful application of the
            program&rsquo;s principles.
          </p>
          <p>
            To maintain active certification, facilitators are expected to
            demonstrate continued engagement with the program during each
            12-month certification period.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <div
          className="mt-12 bg-sand border border-line rounded-md p-8 sm:p-10"
          data-testid="renewal-facilitation-standard"
        >
          <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
            Annual Facilitation Standard
          </h2>
          <div className="mt-6 space-y-5 text-stone2 leading-relaxed">
            <p>
              Certified facilitators must complete a minimum of five
              documented, approved facilitation sessions during their
              certification period.
            </p>
            <p>
              Qualifying activities may include facilitated conversations,
              educational workshops, caregiver discussions, hospice and
              bereavement groups, church and ministry programs, and approved
              virtual learning experiences.
            </p>
            <p>
              Activities may be conducted individually or in groups, provided
              they use authorized program materials and remain within the
              facilitator&rsquo;s approved scope.
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function VerificationOfActivity() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24" data-testid="renewal-verification-of-activity">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Verification of Facilitation Activity
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            To maintain the integrity and credibility of the From Diagnosis to
            Goodbye&trade; Certified Facilitator Program, all qualifying
            facilitation sessions must be supported by verifiable
            documentation.
          </p>
          <p>
            Facilitators must complete a minimum of five approved sessions
            during each 12-month certification period and submit an Annual
            Facilitator Activity Log.
          </p>
          <p>
            Each session must be supported by confirmation from an authorized
            organizational representative or other acceptable independent
            evidence demonstrating that the activity occurred.
          </p>
          <p>
            For independently facilitated virtual or community sessions,
            alternative verification may include dated event announcements,
            registration records, or other documentation approved by the
            certification program.
          </p>
          <p>
            Self-reported activity without acceptable supporting verification
            will not ordinarily qualify toward the annual requirement.
          </p>
        </div>
        <div className="mt-10 max-w-3xl border-l-2 border-gold pl-6">
          <p className="text-stone2 leading-relaxed">
            All documentation must protect participant confidentiality.
            Patient names, medical records, personal grief disclosures, and
            other sensitive information must not be submitted.
          </p>
        </div>
        <div className="mt-8 max-w-3xl space-y-5 text-stone2 leading-relaxed">
          <p>
            The program reserves the right to request clarification or
            additional documentation, verify submitted activities, and reject
            activities that do not meet certification standards.
          </p>
          <p>
            Submission of five sessions does not automatically guarantee
            renewal. Certification remains subject to verification, ethical
            compliance, and completion of all applicable renewal
            requirements.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function RenewalCompetency() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24" data-testid="renewal-competency">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Renewal and Continued Competency
        </p>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight">
          What annual renewal requires.
        </h2>
        <p className="mt-8 max-w-3xl text-lg leading-relaxed text-stone2">
          Annual certification renewal brings together the renewal course
          described above and the Active Facilitation Requirement into one
          complete set of expectations:
        </p>
        <div className="mt-8 max-w-3xl">
          <ul className="space-y-4">
            {[
              "Completion of the Annual Renewal Course (FD2G-FAC-R01), including program updates, renewal learning activities, and the renewal assessment (80% passing score).",
              "Completion of at least five approved facilitation sessions.",
              "Submission of the Annual Facilitator Activity Log.",
              "Reaffirmation of the Facilitator Code of Ethics and Facilitator Agreement.",
              "Submission of the applicable renewal fee.",
            ].map((t) => (
              <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                <span className="shrink-0 font-serif text-gold">—</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-10 max-w-3xl text-stone2 leading-relaxed">
          Renewal is subject to review and approval. Payment alone does not
          guarantee renewal of certification.
        </p>
      </Reveal>
    </section>
  );
}

function ExceptionsExtensions() {
  return (
    <section className="bg-sand border-y border-line" data-testid="renewal-exceptions">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Exceptions and Extensions
          </p>
          <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
            <p>
              Facilitators unable to meet the annual activity requirement
              because of illness, caregiving responsibilities, limited
              program opportunities, or other significant circumstances may
              request an extension or an approved alternative competency
              review.
            </p>
            <p>
              Exceptions and alternative reviews must be reviewed and
              approved rather than automatically granted.
            </p>
            <p>
              The program recognizes that opportunities to facilitate may
              vary. Its goal is to encourage meaningful practice,
              professional accountability, and responsible use of the
              program&mdash;not to penalize those facing circumstances
              beyond their control.
            </p>
          </div>

          <h3
            className="mt-10 font-serif text-xl sm:text-2xl text-navy tracking-tight"
            data-testid="renewal-extensions-status-heading"
          >
            Certification Status During Extensions
          </h3>
          <div className="mt-5 space-y-4 text-stone2 leading-relaxed text-[15px]">
            <p>
              An approved extension to complete outstanding renewal
              requirements does not automatically extend a facilitator&rsquo;s
              active certification or authorization to facilitate
              the program beyond the original expiration date.
            </p>
            <p>
              Any extension of active certification status must be separately
              approved by the program administrator, documented in the
              facilitator&rsquo;s certification record, and assigned a
              specific expiration date.
            </p>
            <p>
              If active-status extension is not expressly granted, the
              facilitator&rsquo;s certification expires on its established
              expiration date, even when additional time has been approved to
              complete renewal requirements.
            </p>
          </div>
          <p className="mt-6 text-stone2 leading-relaxed text-[15px]">
            This distinction protects the integrity of the certification
            while allowing reasonable flexibility for facilitators facing
            exceptional circumstances.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Maintaining Your Certified From Diagnosis to Goodbye Facilitator
          Credential
        </p>
        <h1 className="mt-5 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
          Annual Facilitator Renewal
        </h1>
        <p className="mt-6">
          <span className="inline-block text-xs uppercase tracking-[0.18em] text-mist border border-line-strong rounded-full px-4 py-1.5">
            Renewal Course: FD2G-FAC-R01
          </span>
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            Certification as a From Diagnosis to Goodbye Facilitator is
            issued for 12 months.
          </p>
          <p>
            Annual renewal helps ensure that facilitators continue to
            understand the program's methodology, ethical expectations,
            boundaries, and current standards while responsibly carrying the
            work forward.
          </p>
          <p>
            Renewal is not intended to make active facilitators repeat the
            entire initial certification course each year.
          </p>
          <p>
            Instead, it provides a focused review of the responsibilities
            that accompany continued authorization.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div
          className="mt-16 bg-navy-deep rounded-md px-8 sm:px-14 py-14 sm:py-20 text-center"
          data-testid="renewal-hero-statement"
        >
          <p className="max-w-3xl mx-auto font-serif italic text-2xl sm:text-4xl leading-snug text-ivory">
            &ldquo;Certification is not simply something earned once. It is a{" "}
            <em className="text-gold">responsibility</em> that must continue
            to be carried well.&rdquo;
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function WhyAnnual() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Why Certification Is Renewed Annually
          </p>
          <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
            <p>
              The conversations facilitated through From Diagnosis to
              Goodbye may involve serious illness, caregiving, dying, death,
              grief, family relationships, spiritual questions, and deeply
              personal experiences.
            </p>
            <p>
              Because facilitators are entrusted with these conversations,
              maintaining certification means remaining accountable to the
              standards of the program.
            </p>
          </div>
          <p className="mt-10 text-stone2">Annual renewal helps facilitators:</p>
          <div className="mt-4">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {[
                "Revisit the Five Voices of the Journey",
                "Refresh the NOTICE → LISTEN → UNDERSTAND → RESPOND → HONOR framework",
                "Review ethical responsibilities and facilitator boundaries",
                "Receive applicable program updates",
                "Reaffirm the current Code of Ethics",
                "Reaffirm the current Authorized Facilitator Agreement",
                "Demonstrate continued understanding through a renewal assessment",
              ].map((t) => (
                <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <blockquote
            className="mt-10 max-w-3xl border-l-2 border-gold pl-5 sm:pl-6 font-serif italic text-lg sm:text-xl text-navy leading-snug"
            data-testid="renewal-purpose-principle"
          >
            The purpose of renewal is not to make facilitators start over.
            It is to help them remain prepared, accountable, and current.
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

function WhenBegins() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          When Renewal Begins
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            A facilitator's initial certification remains active for 12
            months from the date certification is issued.
          </p>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-8">
          <div
            className="border border-gold/40 rounded-md px-8 py-6 text-center"
            data-testid="renewal-window-callout"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-mist">
              The renewal window begins approximately
            </p>
            <p className="mt-2 font-serif text-3xl text-gold">
              60 Days Before Expiration
            </p>
          </div>
        </div>
        <p className="mt-10 text-lg text-stone2">
          The program is designed to provide renewal reminders approximately:
        </p>
        <div className="mt-5 flex flex-wrap gap-3" data-testid="renewal-reminder-chips">
          {[
            "60 days before expiration",
            "30 days before expiration",
            "7 days before expiration",
            "At expiration when renewal has not been completed",
          ].map((r) => (
            <span
              key={r}
              className="bg-sand border border-line rounded-full px-5 py-2.5 text-sm text-navy"
            >
              {r}
            </span>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-stone2 leading-relaxed">
          These reminders are intended to help facilitators maintain
          certification without unnecessary interruption.
        </p>
      </Reveal>
    </section>
  );
}

function PartCard({ n, title, children, testid }) {
  return (
    <div
      className="h-full bg-white border border-line rounded-md p-8"
      data-testid={testid}
    >
      <span className="font-serif text-gold text-2xl">{n}</span>
      <h3 className="mt-4 font-serif text-xl sm:text-2xl text-navy leading-snug">
        {title}
      </h3>
      <div className="mt-4 space-y-4 text-stone2 leading-relaxed text-[15px]">
        {children}
      </div>
    </div>
  );
}

function PillLine({ items }) {
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-2">
      {items.map((w, i) => (
        <span key={w} className="flex items-center gap-2">
          <span className="font-serif text-sm text-navy tracking-[0.1em] border border-line-strong rounded-full px-3 py-1 bg-ivory">
            {w}
          </span>
          {i < items.length - 1 && <span className="text-gold font-serif">→</span>}
        </span>
      ))}
    </p>
  );
}

function Course() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            The Annual Renewal Course
          </p>
          <p className="mt-6">
            <span className="inline-block text-xs uppercase tracking-[0.18em] text-mist border border-line-strong rounded-full px-4 py-1.5 bg-ivory">
              FD2G-FAC-R01
            </span>
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight">
            From Diagnosis to Goodbye Facilitator Renewal
          </h2>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-stone2">
            The annual renewal experience is designed to take approximately
            45–60 minutes and focuses on continued competency rather than
            repeating the entire initial training.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          <Reveal className="h-full">
            <PartCard n="1" title="Part One — Return to the Foundation" testid="renewal-part-1">
              <p>
                Review the purpose of the From Diagnosis to Goodbye program,
                the Five Voices of the Journey, and the facilitator's
                responsibility to keep the person's lived experience at the
                center of the conversation.
              </p>
              <p className="text-sm text-mist">Revisit:</p>
              <PillLine
                items={[
                  "Patient",
                  "Family",
                  "Caregiver",
                  "Professional or Helper",
                  "Grief",
                ]}
              />
              <PillLine
                items={["NOTICE", "LISTEN", "UNDERSTAND", "RESPOND", "HONOR"]}
              />
            </PartCard>
          </Reveal>
          <Reveal delay={0.06} className="h-full">
            <PartCard n="2" title="Part Two — Program Updates" testid="renewal-part-2">
              <p>
                Review significant updates to facilitator standards, approved
                resources, program practices, or implementation guidance that
                have occurred since the facilitator's previous certification
                period.
              </p>
              <p>
                If there are no material changes during a particular renewal
                cycle, facilitators may instead receive a concise
                confirmation of the standards that remain in effect.
              </p>
            </PartCard>
          </Reveal>
          <Reveal className="h-full">
            <PartCard n="3" title="Part Three — Ethics, Boundaries & Program Integrity" testid="renewal-part-3">
              <p>Revisit key responsibilities involving:</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {[
                  "Participant autonomy",
                  "Privacy",
                  "Emotional and relational safety",
                  "Spiritual and cultural differences",
                  "Facilitator boundaries",
                  "Professional scope",
                  "Referral and escalation",
                  "Intellectual property",
                  "Accurate representation of certification",
                  "Responsible use of program materials",
                ].map((t) => (
                  <li key={t} className="flex gap-2.5 leading-relaxed">
                    <span className="shrink-0 font-serif text-gold">—</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <p>
                Facilitators reaffirm the current Certified Facilitator Code
                of Ethics and Authorized Facilitator Agreement as part of
                renewal.
              </p>
            </PartCard>
          </Reveal>
          <Reveal delay={0.06} className="h-full">
            <PartCard n="4" title="Part Four — Renewal Assessment" testid="renewal-part-4">
              <p>
                Complete a scenario-based renewal assessment focused on
                practical application of current facilitator standards.
              </p>
              <p>
                <span className="inline-block text-xs uppercase tracking-[0.18em] text-mist border border-line-strong rounded-full px-4 py-1.5 bg-ivory">
                  Passing score: 80%
                </span>
              </p>
              <p>
                Facilitators who do not initially reach the required score
                may review instructional feedback and retake the assessment.
              </p>
            </PartCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Completed() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          When Renewal Is Completed
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            When all current renewal requirements have been satisfied, the
            facilitator's certification period is extended for another 12
            months.
          </p>
          <p>
            The facilitator retains the{" "}
            <span className="font-medium text-navy">
              same individual certification number
            </span>
            .
          </p>
          <p>The certification record should reflect:</p>
        </div>
        <div className="mt-4 max-w-3xl">
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            {[
              "Original certification date",
              "Current certification period",
              "Current expiration date",
              "Certification status",
            ].map((t) => (
              <li key={t} className="flex gap-3 leading-relaxed text-stone2">
                <span className="shrink-0 font-serif text-gold">—</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <blockquote
          className="mt-12 max-w-4xl border-l-2 border-gold pl-6 sm:pl-8 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
          data-testid="renewal-identity-statement"
        >
          &ldquo;The facilitator keeps the credential identity. Renewal keeps
          the authorization current.&rdquo;
        </blockquote>
      </Reveal>
    </section>
  );
}

const LIFECYCLE = [
  {
    s: "ACTIVE",
    d: "Certification is current, and the facilitator may represent themselves as a Certified From Diagnosis to Goodbye Facilitator subject to current program standards.",
  },
  {
    s: "RENEWAL DUE",
    d: "The facilitator is within the designated renewal window. Certification remains active until the current expiration date.",
  },
  {
    s: "EXPIRED",
    d: "The certification period has ended without completed renewal. The facilitator should no longer represent the credential as currently active or exercise authorization that requires active certification until renewal or reinstatement requirements have been satisfied.",
  },
];

function StatusLifecycle() {
  return (
    <section className="bg-navy-deep" data-testid="renewal-lifecycle-band">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Certification Status
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-ivory tracking-tight">
            The active lifecycle.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {LIFECYCLE.map((l, i) => (
            <Reveal key={l.s} delay={0.06 * i} className="h-full">
              <div
                className="h-full border-t-2 border-gold pt-5"
                data-testid={`renewal-status-${l.s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <p className="font-serif text-2xl text-gold tracking-[0.1em]">
                  {l.s}
                </p>
                <p className="mt-4 text-sm text-ivory/80 leading-relaxed">
                  {l.d}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const PATHWAYS = [
  {
    t: "Expired 1–90 Days",
    b: "The facilitator may generally complete the standard annual renewal requirements.",
    b2: "This includes the current renewal content, assessment, Code of Ethics affirmation, Authorized Facilitator Agreement acceptance, and any other current renewal requirements.",
  },
  {
    t: "Expired 91 Days–12 Months",
    b: "The facilitator completes the current renewal requirements plus any applicable reinstatement review or additional requirements established by the program.",
    b2: "This pathway recognizes that more time has passed while still allowing the facilitator to return without automatically repeating the entire initial course.",
  },
  {
    t: "Expired More Than 12 Months",
    b: "The individual must complete the current full Certified Facilitator Training and certification requirements again.",
    b2: "This ensures that someone returning after an extended period is trained according to the current program methodology, standards, and requirements.",
  },
];

function IfExpires() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          If Certification Expires
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            Missing a renewal date does not necessarily mean someone must
            immediately repeat the entire facilitator course.
          </p>
          <p>
            The reinstatement pathway depends on how long certification has
            been expired.
          </p>
        </div>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-3 gap-6">
        {PATHWAYS.map((p, i) => (
          <Reveal key={p.t} delay={0.06 * i} className="h-full">
            <div
              className="h-full bg-white border border-line rounded-md p-8"
              data-testid={`renewal-pathway-${i + 1}`}
            >
              <h3 className="font-serif text-xl sm:text-2xl text-navy leading-snug">
                {p.t}
              </h3>
              <p className="mt-4 text-stone2 leading-relaxed text-[15px]">{p.b}</p>
              <p className="mt-3 text-stone2 leading-relaxed text-[15px]">{p.b2}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <blockquote className="mt-14 max-w-4xl border-l-2 border-gold pl-5 sm:pl-6 font-serif italic text-lg sm:text-xl text-navy leading-snug">
          The longer certification has been inactive, the greater the
          responsibility to demonstrate current preparation before
          authorization is restored.
        </blockquote>
      </Reveal>
    </section>
  );
}

function OtherStatuses() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Other Certification Statuses
          </p>
          <p className="mt-6 max-w-3xl text-lg text-stone2 leading-relaxed">
            In addition to ACTIVE, RENEWAL DUE, and EXPIRED, a certification
            record may reflect:
          </p>
        </Reveal>
        <div className="mt-8 max-w-4xl">
          {[
            { s: "SUSPENDED", d: "Certification authorization has been temporarily suspended under applicable program standards." },
            { s: "REVOKED", d: "Certification authorization has been withdrawn under applicable program standards." },
            { s: "VOLUNTARILY INACTIVE", d: "The facilitator has chosen not to maintain active certification." },
          ].map((s) => (
            <Reveal key={s.s}>
              <div
                className="border-t border-line py-5"
                data-testid={`renewal-status-${s.s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <p className="font-serif text-xl text-navy tracking-[0.08em]">{s.s}</p>
                <p className="mt-1 text-stone2 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-3xl text-stone2 leading-relaxed">
            Renewal alone does not automatically reverse a suspension or
            revocation. Any applicable program requirements must first be
            satisfied.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function EthicsRenewal() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Renewal and the Code of Ethics
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight leading-[1.15]">
            Annual renewal is also an opportunity to reaffirm the
            responsibilities that accompany the facilitator role.
          </h2>
          <p className="mt-8 text-stone2">
            Certified facilitators continue to commit to:
          </p>
          <div className="mt-4">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {[
                "Protecting the person's voice",
                "Respecting autonomy",
                "Facilitating without judgment",
                "Honoring spiritual and cultural differences",
                "Protecting privacy",
                "Maintaining appropriate boundaries",
                "Recognizing when facilitation is not enough",
                "Protecting program integrity",
                "Respecting intellectual property",
                "Representing certification accurately",
                "Maintaining current certification",
                "Using the facilitator role with integrity",
              ].map((t) => (
                <li key={t} className="flex gap-3 leading-relaxed text-stone2 text-[15px]">
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-8 text-stone2 leading-relaxed">
            The current Code of Ethics presented during renewal governs the
            facilitator's next certification period.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-sand border border-line rounded-md p-8">
            <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
              Renewal and Professional Scope
            </p>
            <div className="mt-5 space-y-4 text-stone2 leading-relaxed text-[15px]">
              <p>
                Renewing a Certified From Diagnosis to Goodbye Facilitator
                credential maintains authorization to facilitate the approved
                program.
              </p>
              <p>It does not expand the facilitator's independent professional scope.</p>
              <p>
                Renewal does not confer or renew licensure or credentials in
                counseling, psychotherapy, healthcare, chaplaincy, ministry,
                crisis intervention, or any other regulated or professional
                discipline.
              </p>
              <p>
                Individuals who separately hold professional credentials
                remain responsible for maintaining those credentials through
                their applicable licensing, certification, professional, or
                organizational requirements.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StayingCurrent() {
  return (
    <section className="bg-navy-deep">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Staying Current Protects the Work
          </p>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-ivory/70 leading-relaxed">
            Annual renewal exists for more than administrative
            recordkeeping. It helps protect:
          </p>
          <div className="mt-8 space-y-2 font-serif italic text-2xl sm:text-3xl text-ivory">
            <p>The participant.</p>
            <p>The facilitator.</p>
            <p>The integrity of the program.</p>
            <p>The voices entrusted to the room.</p>
          </div>
          <p className="mt-10 max-w-3xl mx-auto text-ivory/70 leading-relaxed">
            A facilitator who remains current demonstrates an ongoing
            commitment to carry From Diagnosis to Goodbye according to the
            standards under which the work was entrusted to them.
          </p>
          <p className="mt-10 max-w-2xl mx-auto font-serif text-xl text-gold leading-relaxed">
            Certification is not simply a credential to display. It is a
            responsibility to help others feel seen, heard, respected, and
            supported.
          </p>
          <p className="mt-6 font-serif italic text-lg text-ivory/80">
            See the person. Hear the voice. Honor the journey.
          </p>
          <p
            className="mt-12 font-serif text-3xl sm:text-5xl text-gold tracking-tight"
            data-testid="renewal-certified-statement"
          >
            Certified. Current. Accountable.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Maintain Your Certification
        </p>
        <p className="mt-8 max-w-2xl mx-auto text-lg text-stone2 leading-relaxed">
          The annual renewal process will become available to Certified From
          Diagnosis to Goodbye Facilitators as the certification system is
          implemented.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/facilitators/certification"
            data-testid="renewal-cta-certification"
            className={ctaGoldCls}
          >
            Review Certification Requirements <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/facilitators/training"
            data-testid="renewal-cta-training"
            className={ctaCls}
          >
            Explore Facilitator Training <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Link
          to="/facilitators"
          data-testid="renewal-back"
          className="mt-10 inline-flex items-center gap-2 text-sm text-stone2 hover:text-gold transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Facilitator Program
          Overview
        </Link>
      </Reveal>
    </section>
  );
}

export default function FacilitatorRenewal() {
  return (
    <div className="bg-ivory" data-testid="facilitators-renewal-page">
      <Hero />
      <WhyAnnual />
      <WhenBegins />
      <ActiveFacilitation />
      <VerificationOfActivity />
      <Course />
      <RenewalCompetency />
      <ExceptionsExtensions />
      <Completed />
      <StatusLifecycle />
      <IfExpires />
      <OtherStatuses />
      <EthicsRenewal />
      <StayingCurrent />
      <Closing />
    </div>
  );
}
